import { OiAnalysisOrderGrouped } from "../../../services/AnalysisOrderService";
import { OiPowderOrderGrouped } from "../../../services/PowderOrderService";
import type { AnalysisResultSection } from "../../../services/AnalysisResultService";
import { PreparationAndRoutingData, PreparationRoutingItem } from "../redux/analysisOrder";
import { getAnalysisDefinition } from "./analysisConfig";

/** A result can only be completed once it has at least one analysis and every analysis is marked complete. */
export function areAllSectionsComplete(sections: Pick<AnalysisResultSection, "completion">[]) {
  return sections.length > 0 && sections.every(section => section.completion.isComplete);
}

type PreparationAndRoutingInput = {
  comment?: string;
  selectedPreparationMethods?: string[];
  noPreparationRequired?: boolean;
  routes?: Record<string, Partial<PreparationRoutingItem> | null>;
};

export interface PowderOrderLookupOption {
  uuid: string;
  orderId: string;
}

export function flattenPowderOrderOptions(grouped: OiPowderOrderGrouped | null): PowderOrderLookupOption[] {
  const options = [
    ...(grouped?.drafts ?? []),
    ...(grouped?.nonDrafts ?? []),
  ]
    .filter((item): item is { uuid: string; orderId?: string } => Boolean(item?.uuid && item.orderId))
    .map(item => ({ uuid: item.uuid, orderId: item.orderId || "" }));

  const unique = new Map<string, PowderOrderLookupOption>();
  options.forEach(option => {
    if (!unique.has(option.uuid)) {
      unique.set(option.uuid, option);
    }
  });

  return Array.from(unique.values()).sort((left, right) => right.orderId.localeCompare(left.orderId));
}

export function buildSampleId(linkedPowderOrderLabel: string, index: number): string {
  const prefix = linkedPowderOrderLabel.trim() || "UNLINKED-POWDER";
  return `${prefix}_${index + 1}`;
}

/** Next sample id for a powder order: numbering restarts at 1 for every linked powder order. */
export function buildNextSampleId(
  linkedPowderOrderLabel: string,
  samples: Array<{ sampleId: string; linkedPowderOrderId: string }>,
): string {
  const prefix = linkedPowderOrderLabel.trim();
  const highestIndex = samples
    .filter(sample => sample.linkedPowderOrderId === prefix && sample.sampleId.startsWith(`${prefix}_`))
    .map(sample => Number(sample.sampleId.slice(prefix.length + 1)))
    .filter(Number.isInteger)
    .reduce((highest, index) => Math.max(highest, index), 0);
  return buildSampleId(prefix, highestIndex);
}

/** Analyses with a single route option have it selected from the start. */
export function defaultRouteSelection(analysisKey: string): PreparationRoutingItem {
  const routeOptions = getAnalysisDefinition(analysisKey)?.routeOptions ?? [];
  return { selectedOptions: routeOptions.length === 1 ? [...routeOptions] : [] };
}

export function normalizeSamples(
  _linkedPowderOrderLabel: string,
  samples: Array<{ sampleId: string; linkedPowderOrderId: string; coPercentage: number | null }>,
) {
  return samples.map((sample, index) => ({
    ...sample,
    sampleId: sample.linkedPowderOrderId ? sample.sampleId || buildSampleId(sample.linkedPowderOrderId, index) : sample.sampleId,
  }));
}

function splitLegacyDetails(details?: string) {
  return (details ?? "")
    .split(",")
    .map(option => option.trim())
    .filter(Boolean);
}

export function normalizePreparationRoutingItem(item?: Partial<PreparationRoutingItem> | null): PreparationRoutingItem {
  const selectedOptions = item?.selectedOptions;
  if (Array.isArray(selectedOptions)) {
    return { selectedOptions: selectedOptions.filter(Boolean) };
  }

  if (item?.details) {
    return { selectedOptions: splitLegacyDetails(item.details) };
  }

  return { selectedOptions: [] };
}

export function normalizePreparationAndRoutingData(
  data?: PreparationAndRoutingInput | null,
  selectedAnalyses: string[] = [],
): PreparationAndRoutingData {
  const routes = Object.fromEntries(
    Object.entries(data?.routes ?? {}).map(([analysisKey, item]) => [analysisKey, normalizePreparationRoutingItem(item)]),
  ) as Record<string, PreparationRoutingItem>;

  selectedAnalyses.forEach(analysisKey => {
    if (!routes[analysisKey]) {
      routes[analysisKey] = defaultRouteSelection(analysisKey);
    }
  });

  const selectedPreparationMethods = data?.selectedPreparationMethods;

  return {
    comment: data?.comment ?? "",
    selectedPreparationMethods: Array.isArray(selectedPreparationMethods)
      ? selectedPreparationMethods.filter(Boolean)
      : [],
    noPreparationRequired: data?.noPreparationRequired ?? false,
    routes,
  };
}

export function getReviewStatusLabel(order: {
  isDraft: boolean;
  isSubmittedForReview: boolean;
  reviewPassed: boolean | null;
}) {
  if (order.isDraft) {
    return "Draft";
  }

  if (order.reviewPassed === true) {
    return "Reviewed";
  }

  if (order.reviewPassed === false) {
    return "Failed review";
  }

  if (order.isSubmittedForReview) {
    return "Ready for review";
  }

  return "In progress";
}

export function countGroupedAnalysisOrders(grouped: OiAnalysisOrderGrouped | null) {
  return {
    drafts: grouped?.drafts.length ?? 0,
    readyForReview: grouped?.readyForReview.length ?? 0,
    reviewed: grouped?.reviewed.length ?? 0,
    failedReview: grouped?.failedReview.length ?? 0,
    cancelled: grouped?.cancelled.length ?? 0,
  };
}